import { createRouter, createWebHistory } from 'vue-router';
import { trackPageView } from '@/utils/gtag';
import { useProviderOnboardingStore } from '@/store/providerOnboardingStore';
import AdminLayout from '@/layouts/AdminLayout.vue';
import DefaultLayout from '@/layouts/DefaultLayout.vue';
import HomePage from '@/views/HomePage.vue';
import Login from '@/components/Login.vue';
import ForgotPassword from '@/components/ForgotPassword.vue';
import RegisterUser from '@/components/RegisterUser.vue';
import OtpValidation from '@/components/OtpValidation.vue';
import Result from '@/components/Result.vue';
import Confirmation from '@/components/Confirmation.vue';
import ContactForm from '@/components/ContactForm.vue';
import ProjectsPage from '@/components/ProjectsPage.vue';
import RegisterProject from '@/components/RegisterProject.vue';
import Project from '@/components/Project.vue';
import ProjectList from '@/components/ProjectList.vue';
import ProposalList from '@/components/ProposalList.vue';
import FavoriteList from '@/components/FavoriteList.vue';
import UserProfile from '@/components/UserProfile.vue';
import FreelancerProfile from '@/components/FreelancerProfile.vue';
import ProjectPayment from '@/components/ProjectPayment.vue';
import Dispute from '@/components/Dispute.vue';

// V2 — Serviços Presenciais
import ProviderSearch from '@/components/ProviderSearch.vue';
import ProvidersByCategory from '@/components/ProvidersByCategory.vue';
import ProviderPublicProfile from '@/components/ProviderPublicProfile.vue';
import BookingPage from '@/components/BookingPage.vue';
import AppointmentDetails from '@/components/AppointmentDetails.vue';
import AppointmentPayment from '@/components/AppointmentPayment.vue';
import AppointmentList from '@/components/AppointmentList.vue';
import ProviderSchedule from '@/components/ProviderSchedule.vue';
import ProviderServices from '@/components/ProviderServices.vue';

const PUBLIC_ROUTES = [
  'login',
  'forgot-password',
  'register',
  'otp-validation',
  'HomePage',
  'provider-search',
  'providers-by-category',
  'provider-profile',
];

const ONBOARDING_ALLOWED_ROUTES = [
  'profile',
  'provider-services',
  'provider-schedule',
  'login',
  'register',
  'forgot-password',
  'otp-validation',
  'HomePage',
  'provider-search',
  'providers-by-category',
  'provider-profile',
];

const routes = [
  {
    path: '/login',
    name: 'login',
    component: Login,
  },
  {
    path: '/esqueceu-senha',
    name: 'forgot-password',
    component: ForgotPassword,
  },
  {
    path: '/cadastro',
    name: 'register',
    component: RegisterUser,
  },
  {
    path: '/validar-codigo',
    name: 'otp-validation',
    component: OtpValidation,
  },
  {
    path: '/confirmacao/:reference/:hash',
    name: 'confirmation',
    component: Confirmation,
  },
  {
    path: '/',
    component: DefaultLayout,
    children: [
      {
        path: '',
        name: 'HomePage',
        component: HomePage,
      },
      {
        path: "contato",
        name: "Contato",
        component: ContactForm,
      },
      {
        path: 'projeto/:hash/:slug',
        name: 'detalhes',
        component: Project,
        props: true
      },
      {
        path: 'cadastre-seu-projeto',
        name: 'register-project',
        component: RegisterProject,
        meta: { requiresAuth: true },
      },
      {
        path: 'projetos/:hash/:slug/pagamento',
        name: 'project-payment',
        component: ProjectPayment,
      },
      {
        path: 'payment/result',
        name: 'PaymentResult',
        component: Result,
      },
      {
        path: 'disputas/:id',
        name: 'dispute',
        component: Dispute,
        meta: { requiresAuth: true }
      },
      {
        path: 'projetos',
        name: 'projects',
        component: ProjectsPage,
        meta: { requiresAuth: true },
      },
      {
        path: 'meus-projetos',
        name: 'project-list',
        component: ProjectList,
        meta: { requiresAuth: true },
      },
      {
        path: 'minhas-propostas',
        name: 'proposal-list',
        component: ProposalList,
        meta: { requiresAuth: true },
      },
      {
        path: 'favoritos',
        name: 'favorite-list',
        component: FavoriteList,
        meta: { requiresAuth: true },
      },
      {
        path: 'perfil',
        name: 'profile',
        component: UserProfile,
        meta: { requiresAuth: true },
      },
      {
        path: 'freelancer/:hash/:reference',
        name: 'freelancer',
        component: FreelancerProfile,
        meta: { requiresAuth: true },
      },

      // ========================
      // V2 — Serviços Presenciais
      // ========================
      {
        path: 'buscar-prestadores',
        name: 'provider-search',
        component: ProviderSearch,
      },
      {
        path: 'buscar-prestadores/categoria/:categoryId',
        name: 'providers-by-category',
        component: ProvidersByCategory,
      },
      {
        path: 'prestador/:hash',
        name: 'provider-profile',
        component: ProviderPublicProfile,
      },
      {
        path: 'agendar/:hash/:serviceId',
        name: 'booking',
        component: BookingPage,
        meta: { requiresAuth: true },
      },
      {
        path: 'agendamento/:id',
        name: 'appointment-details',
        component: AppointmentDetails,
        meta: { requiresAuth: true },
      },
      {
        path: 'agendamento/:id/pagamento',
        name: 'appointment-payment',
        component: AppointmentPayment,
        meta: { requiresAuth: true },
      },
      {
        path: 'meus-agendamentos',
        name: 'appointment-list',
        component: AppointmentList,
        meta: { requiresAuth: true },
      },
      {
        path: 'minha-agenda',
        name: 'provider-schedule',
        component: ProviderSchedule,
        meta: { requiresAuth: true },
      },
      {
        path: 'meus-servicos',
        name: 'provider-services',
        component: ProviderServices,
        meta: { requiresAuth: true },
      },
    ],
  },
  {
    path: '/app',
    component: AdminLayout,
    meta: { requiresAuth: true, adminOnly: true }, // 🔒 exige autenticação e role admin/administrative
    children: [
      /*{ path: 'dashboard', name: 'Dashboard', component: Dashboard },
      */
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Atualiza canonical e noindex a cada mudança de rota.
// Em SPAs o index.html é compartilhado por todas as rotas, então o canonical
// e o robots precisam ser gerenciados via JavaScript.
router.afterEach((to) => {
  trackPageView(to.fullPath, String(to.name || ''));

  // --- Canonical ---
  // Remove qualquer trailing slash da path (exceto a raiz "/")
  const canonicalPath = to.path === '/' ? '/' : to.path.replace(/\/$/, '');
  const canonicalUrl = `https://getlance.com.br${canonicalPath}`;

  let canonicalEl = document.querySelector('link[rel="canonical"]');
  if (!canonicalEl) {
    canonicalEl = document.createElement('link');
    canonicalEl.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalEl);
  }
  canonicalEl.setAttribute('href', canonicalUrl);

  // --- Robots / noindex ---
  // Rotas com requiresAuth não devem ser indexadas: o Google não consegue logar
  // para ver o conteúdo, então indexar essas URLs não gera valor e polui o índice.
  // Importante: o robots.txt NÃO bloqueia essas URLs — se bloqueasse, o Google
  // nunca leria esta meta tag. A combinação correta é: robots.txt permite rastrear,
  // e a meta tag instrui a não indexar.
  const requiresAuth = to.matched.some(r => r.meta.requiresAuth);
  let robotsEl = document.querySelector('meta[name="robots"]');
  if (!robotsEl) {
    robotsEl = document.createElement('meta');
    robotsEl.setAttribute('name', 'robots');
    document.head.appendChild(robotsEl);
  }
  if (requiresAuth) {
    robotsEl.setAttribute('content', 'noindex,nofollow');
  } else {
    robotsEl.setAttribute('content', 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1');
  }
});

router.beforeEach(async (to, from, next) => {
  const raw = localStorage.getItem('userData');

  let userData = null;
  try {
    userData = raw ? JSON.parse(raw) : null;
  } catch {
    userData = null;
    localStorage.removeItem('userData'); // evita ficar “travado” com dado inválido
  }

  // ideal: valide por token, não só “existe userData”
  const token = userData?.token || userData?.access_token || userData?.user?.token;
  const isAuthenticated = !!token;

  const role = userData?.user?.role;
  const requiresAuth = to.matched.some(r => r.meta.requiresAuth);
  const adminOnly = to.matched.some(r => r.meta.adminOnly);

  if (requiresAuth && !isAuthenticated) {
    return next({ name: 'login', query: { redirect: to.fullPath } });
  }

  if (adminOnly && !['admin', 'administrative'].includes(role)) {
    return next({ name: 'HomePage' });
  }

  // Onboarding check — carrega store para todo usuário autenticado.
  if (isAuthenticated) {
    const onboardingStore = useProviderOnboardingStore();
    await onboardingStore.load();

    if (onboardingStore.isPresential && !ONBOARDING_ALLOWED_ROUTES.includes(to.name)) {
      if (onboardingStore.needsLocation) {
        return next({ name: 'profile' });
      }
      if (onboardingStore.needsServices) {
        return next({ name: 'provider-services' });
      }
      if (onboardingStore.needsSchedule) {
        return next({ name: 'provider-schedule' });
      }
    }
  }

  next();
});

export default router;
